/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactElement, useState, useCallback } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { Questionnaire } from '@userzoomplatform/pe-study-tasks/dist/lib';
import { useHistory, useLocation } from 'react-router-dom';
import TaskLoader from '../../components/task-loader';
import { QUESTIONNAIRE_FIELDS } from '../task/fragments';
import { useToken } from '../../hooks';
import { gqlQuestionTypes } from '../../utils/constants';
import { getRedirectUrlFromStatus } from '../../utils/status-router';
import config from '../../utils/config';

/* eslint-disable @typescript-eslint/no-explicit-any */

const customizationQuery = gql`
	query customization {
		customization {
			name
			systemTexts {
				mandatoryQuestion {
					ratingScale
					choiceSingle
					choiceMatrixOrMultiple
					multipleMinimum
					multipleMaximum
					multipleExact
					pageError
				}
				buttons {
					next
					exitLink
				}
				miscellaneous {
					choiceDropdownDefault
				}
			}
		}
	}
`;

const answerPageMutation = gql`
	${QUESTIONNAIRE_FIELDS}

	mutation ($answer: AnswerPage!) {
		answerQuestionnairePage(answer: $answer) {
			...QuestionnaireFields
			... on Finished {
				url
			}
			... on QuestionnaireOutcome {
				completed
			}
		}
	}
`;

const taskQuery = gql`
	${QUESTIONNAIRE_FIELDS}
	query {
		screener {
			...QuestionnaireFields
			... on Finished {
				url
			}
			... on QuestionnaireOutcome {
				completed
			}
		}
	}
`;

const Screener = (): ReactElement => {
	const { assetsBasePath } = config();
	const { loading: loadingCustomization, data: dataCustomization } = useQuery<any>(customizationQuery);
	const token = useToken();
	const history = useHistory();
	const location = useLocation();
	const isModerated = location.pathname.includes('/moderated');

	const handleResponse = useCallback(
		(data: any) => {
			if (data?.completed && token) {
				history.replace(getRedirectUrlFromStatus('qualified', token, isModerated));
			} else if (data?.url) {
				window.location.href = data?.url;
			} else {
				setScreenerData(data);
			}
		},
		[history, token, isModerated]
	);

	const { loading } = useQuery<any>(taskQuery, {
		fetchPolicy: 'no-cache',
		onCompleted: (response: any) => {
			handleResponse(response?.screener);
		},
	});

	const [screenerData, setScreenerData] = useState<any>();

	const [answerPage] = useMutation<any>(answerPageMutation, {
		onCompleted: (response: any) => {
			handleResponse(response?.answerQuestionnairePage);
		},
	});

	const onAnswerPage = useCallback(
		async (answers: any) => {
			answerPage({
				variables: {
					answer: {
						pageId: screenerData.page.number,
						questionnaireId: screenerData.id,
						answers: answers.map(({ type, ...answer }: any) => ({
							type: gqlQuestionTypes[type],
							[gqlQuestionTypes[type]]: answer,
						})),
					},
				},
			});
		},
		[screenerData, answerPage]
	);

	if (loading || loadingCustomization) {
		return (
			<div className="task task-loading">
				<TaskLoader />
			</div>
		);
	}

	const texts = {
		systemTexts: dataCustomization.customization.systemTexts,
		copies: {
			optionalQuestionLabel: 'Optional',
			backButton: 'Back',
		},
	};

	if (screenerData) {
		return (
			<Questionnaire
				id={screenerData.id}
				studyName={dataCustomization.customization.name}
				page={screenerData.page}
				onAnswerPage={onAnswerPage}
				onBackPage={() => null}
				onExit={() => window.close()}
				texts={texts}
				config={{ assetsBasePath }}
			/>
		);
	}

	return <div id="screener-empty" />;
};

export default Screener;
